import { debounce, isEmpty } from 'lodash-es';
import { ColumnPinningState } from '@tanstack/react-table';
import * as XLSX from 'xlsx';
import { downloadBlob } from 'utils/download-blob';

export interface CsvExportColumn {
  key: string;
  header: string;
  formatter?: (value: any) => string;
}

export interface CsvExportOptions {
  filename: string;
  columns: CsvExportColumn[];
  data: Record<string, any>[];
  sortFn?: (a: Record<string, any>, b: Record<string, any>) => number;
}

type TableCustomization =
  | {
      columnSizes: Record<string, number>;
      columnVisibility: Record<string, boolean>;
      columnOrder?: string[];
      columnPinning?: ColumnPinningState;
      isColumnFilterActive: boolean;
    }
  | undefined;

export function fetchTableCustomization(tableId: string) {
  const tableCustomization = JSON.parse(
    localStorage.getItem(tableId) ?? '{}'
  ) as TableCustomization;
  return tableCustomization;
}

export function fetchColumnSizes(tableId: string) {
  const tableCustomization = fetchTableCustomization(tableId);
  return tableCustomization?.columnSizes ?? {};
}

export const fetchColumnOrder = (tableId: string) => {
  const tableCustomization = fetchTableCustomization(tableId);
  return tableCustomization?.columnOrder ?? [];
};
export const fetchColumnPinning = (tableId: string) => {
  const tableCustomization = fetchTableCustomization(tableId);
  return tableCustomization?.columnPinning ?? { left: [] };
};

export const updateColumnSizes = debounce(
  (tableId: string, updatedSizes: Record<string, number>) => {
    const tableCustomization = fetchTableCustomization(tableId) ?? {
      columnSizes: {},
    };
    tableCustomization.columnSizes = {
      ...(isEmpty(updatedSizes) ? {} : tableCustomization.columnSizes),
      ...updatedSizes,
    };
    localStorage.setItem(tableId, JSON.stringify(tableCustomization));
  },
  500
);

export const updateColumnOrder = debounce(
  (tableId: string, updatedOrder: string[]) => {
    const tableCustomization = fetchTableCustomization(tableId) ?? {
      columnOrder: [],
    };
    tableCustomization.columnOrder = updatedOrder;
    localStorage.setItem(tableId, JSON.stringify(tableCustomization));
  },
  500
);

export const updateColumnPinning = debounce(
  (tableId: string, updatedPinning: ColumnPinningState) => {
    const tableCustomization = fetchTableCustomization(tableId) ?? {
      columnPinning: { left: [] },
    };
    tableCustomization.columnPinning = updatedPinning;
    localStorage.setItem(tableId, JSON.stringify(tableCustomization));
  },
  500
);

export function fetchColumnVisibility(tableId: string) {
  const tableCustomization = fetchTableCustomization(tableId);
  return tableCustomization?.columnVisibility ?? {};
}

export function updateColumnVisibility(
  tableId: string,
  updatedVisibleColumns: Record<string, boolean>
) {
  const tableCustomization = fetchTableCustomization(tableId) ?? {
    columnVisibility: {},
  };
  tableCustomization.columnVisibility = {
    ...(isEmpty(updatedVisibleColumns)
      ? {}
      : tableCustomization.columnVisibility),
    ...updatedVisibleColumns,
  };

  localStorage.setItem(tableId, JSON.stringify(tableCustomization));
}

export function fetchIsColumnFilterActive(tableId: string) {
  const tableCustomization = fetchTableCustomization(tableId);
  return tableCustomization?.isColumnFilterActive ?? false;
}

export function updateIsColumnFilterActive(
  tableId: string,
  isColumnFilterActive: boolean
) {
  const tableCustomization = fetchTableCustomization(tableId) ?? {
    isColumnFilterActive: false,
  };
  tableCustomization.isColumnFilterActive = isColumnFilterActive;

  localStorage.setItem(tableId, JSON.stringify(tableCustomization));
}

// ─────────────────────────────────────
// Column Order Utilities
// ─────────────────────────────────────

/**
 * Inserts missing columns into an existing column order at their correct positions
 * based on a reference order (initialColumnOrder).
 *
 * When users have a persisted column order and new columns are added (e.g., via a toggle),
 * this ensures new columns appear in their logical position rather than being appended at the end.
 *
 * @param {string[]} existingOrder - The current column order (e.g., from localStorage)
 * @param {string[]} referenceOrder - The initial/default column order that defines correct positions
 * @param {string[]} columnsToInsert - Columns that need to be inserted into existingOrder
 * @return {string[]} New column order with missing columns inserted at correct positions
 */
export function insertColumnsAtCorrectPosition(
  existingOrder: string[],
  referenceOrder: string[],
  columnsToInsert: string[]
): string[] {
  if (columnsToInsert.length === 0) {
    return existingOrder;
  }

  const mergedOrder = [...existingOrder];

  for (const column of columnsToInsert) {
    const targetIndex = referenceOrder.indexOf(column);
    // Find the best position: after the previous column in referenceOrder
    // that exists in mergedOrder, or append at end if no prior column found
    let insertIndex = mergedOrder.length;

    for (let i = targetIndex - 1; i >= 0; i--) {
      const prevCol = referenceOrder[i];
      const prevIndex = mergedOrder.indexOf(prevCol);
      if (prevIndex !== -1) {
        insertIndex = prevIndex + 1;
        break;
      }
    }

    mergedOrder.splice(insertIndex, 0, column);
  }

  return mergedOrder;
}

/**
 * Merges a persisted column order with an updated initial column order.
 * Handles both adding new columns and removing columns that are no longer valid.
 *
 * Use case: When a toggle shows/hides columns, this updates the persisted order
 * to reflect the new column set while preserving user's custom ordering.
 *
 * @param {string[]} currentOrder - The current persisted column order
 * @param {string[]} newInitialOrder - The new initial column order (defines valid columns and their default positions)
 * @param {string[]} prevInitialOrder - The previous initial column order (to detect what changed)
 * @return {string[]} Updated column order with added columns inserted and removed columns filtered out
 */
export function mergeColumnOrder(
  currentOrder: string[],
  newInitialOrder: string[],
  prevInitialOrder: string[]
): string[] {
  const addedColumns = newInitialOrder.filter(
    (col) => !prevInitialOrder.includes(col)
  );
  const removedColumns = prevInitialOrder.filter(
    (col) => !newInitialOrder.includes(col)
  );

  if (addedColumns.length === 0 && removedColumns.length === 0) {
    return currentOrder;
  }

  let newColumnOrder = [...currentOrder];

  // Remove columns that are no longer in the initial order
  if (removedColumns.length > 0) {
    newColumnOrder = newColumnOrder.filter(
      (col) => !removedColumns.includes(col)
    );
  }

  // Insert added columns at their correct positions
  const columnsToInsert = addedColumns.filter(
    (col) => !newColumnOrder.includes(col)
  );

  return insertColumnsAtCorrectPosition(
    newColumnOrder,
    newInitialOrder,
    columnsToInsert
  );
}

// ─────────────────────────────────────
// CSV Export
// ─────────────────────────────────────

const csvQuote = (value: unknown) => `"${String(value).replace(/"/g, '""')}"`;

// Numbers, formatted or not, are not formulas.
const NUMERIC_TEXT = /^[-+]?\$?(\d[\d,]*)?\.?\d+%?$/;

// A spreadsheet runs a cell that starts with a formula character.
const csvCell = (value: unknown) =>
  csvQuote(
    typeof value === 'string' &&
      value.length > 1 &&
      /^[=+\-@\t\r]/.test(value) &&
      !NUMERIC_TEXT.test(value)
      ? `'${value}`
      : value
  );

export function exportTableToCsv(options: CsvExportOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const { filename, columns, data, sortFn } = options;

      if (!data || !data.length) {
        reject(new Error('No data to export'));
        return;
      }

      // Sort data if sort function provided
      const sortedData = sortFn ? [...data].sort(sortFn) : data;

      // Create header row
      const headerRow = columns.map((col) => csvQuote(col.header)).join(',');

      // Create data rows
      const dataRows = sortedData
        .map((row) =>
          columns
            .map((col) => {
              const value = row[col.key];
              const formattedValue = col.formatter
                ? col.formatter(value)
                : (value ?? '');
              return csvCell(formattedValue);
            })
            .join(',')
        )
        .join('\n');

      // Create CSV content (BOM ensures Excel detects UTF-8)
      const csvContent = `\uFEFF${headerRow}\n${dataRows}`;

      // Create and download file
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });

      const dateString = new Date()
        .toLocaleDateString('en-CA')
        .replace(/-/g, '.');

      downloadBlob(blob, `${filename}_${dateString}.csv`);

      resolve();
    } catch (error) {
      reject(error);
    }
  });
}

// ─────────────────────────────────────
// XLSX Export
// ─────────────────────────────────────

export interface XlsxExportColumn {
  key: string;
  header: string;
  formatter?: (value: any) => string | number;
}

export interface XlsxExportSheet {
  name: string;
  columns: XlsxExportColumn[];
  data: Record<string, any>[];
}

export interface XlsxExportOptions {
  filename: string;
  /** Single sheet export - provide columns + data */
  columns?: XlsxExportColumn[];
  data?: Record<string, any>[];
  sheetName?: string;
  /** Multi-sheet export - provide sheets array */
  sheets?: XlsxExportSheet[];
}

function createXlsxWorksheet(
  columns: XlsxExportColumn[],
  data: Record<string, any>[]
): XLSX.WorkSheet {
  const headers = columns.map((col) => col.header);
  const dataRows = data.map((row) =>
    columns.map((col) => {
      const value = row[col.key];
      return col.formatter ? col.formatter(value) : (value ?? '');
    })
  );
  return XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
}

export function exportTableToXlsx(options: XlsxExportOptions): void {
  const { filename, columns, data, sheetName = 'Data', sheets } = options;

  const workbook = XLSX.utils.book_new();

  // Multi-sheet mode
  if (sheets && sheets.length > 0) {
    sheets.forEach((sheet) => {
      const worksheet = createXlsxWorksheet(sheet.columns, sheet.data);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
    });
  }
  // Single-sheet mode
  else if (columns && data) {
    if (!data.length) {
      throw new Error('No data to export');
    }
    const worksheet = createXlsxWorksheet(columns, data);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  } else {
    throw new Error('Must provide either columns+data or sheets');
  }

  // Download with date suffix
  const dateString = new Date().toLocaleDateString('en-CA').replace(/-/g, '.');
  XLSX.writeFile(workbook, `${filename}_${dateString}.xlsx`);
}
